import React, { useState, useEffect } from "react";
import { useAdminContext } from "../Hooks/AdminContext";
import {
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import { styled } from "@mui/styles";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FormInput from "../components/GeneralComponents/FormInput";
import { loginLogo } from "../assets";
import {
  _Website_Settings_List,
  _Website_Settings_List_Update,
} from "../DAL/Listing/WebsiteSetting"; // Assuming an API function exists
import { enqueueSnackbar } from "notistack";
import CustomCircularProgress from "../components/CustomCircularProgress";
import { ImageUpload } from "../DAL/Login/Login";
import { picBaseUrl } from "../config/config";

const Input = styled("input")({
  display: "none",
});

const DefaultSetting = () => {
  const [loading, setLoading] = useState(true);
  const [Logo, setLogo] = useState(null);
  const [favIcon, setFavicon] = useState(null);
  const { setnavbarTitle } = useAdminContext();
  const [localInput, setLocalInput] = useState({
    top_up_fee: "",
    affiliate_commission: "",
    meta_title: "",
    meta_description: "",
    logo: null,
    favicon: null,
    auto_responder_message: "",
  });

  const handleChange = (e, name) => {
    setLocalInput({ ...localInput, [name]: e.target.value });
  };

  const fileChangedHandler = (name, e) => {
    const file = e.target.files[0];

    if (!file) return;

    // Allowed image types
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar("Only JPG, JPEG, PNG, and WEBP files are allowed.", {
        variant: "error",
      });
      return;
    }

    // Set preview for logo or favicon
    if (name === "logo") {
      setLogo(URL.createObjectURL(file));
    }

    if (name === "favicon") {
      setFavicon(URL.createObjectURL(file));
    }

    // Update state with the file
    setLocalInput({
      ...localInput,
      [name]: file,
    });
  };

  const getDefaultSettings = async () => {
    setLoading(true);
    const response = await _Website_Settings_List({ type: "general" });
    if (response.code === 200) {
      setLocalInput({
        ...response.settings,
        top_up_fee: response?.settings?.topup_fee,
      });
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
    }
    setLoading(false);
  };

  useEffect(() => {
    setnavbarTitle("General Settings");
    getDefaultSettings();
  }, []);

  const uploadImage = async (selectedImage) => {
    if (selectedImage && typeof selectedImage !== "string") {
      const formData = new FormData();
      formData.append("image", selectedImage);

      const uploadResponse = await ImageUpload(formData);
      if (uploadResponse.code === 200) {
        return uploadResponse.path;
      } else {
        enqueueSnackbar(uploadResponse.message, { variant: "error" });
        return null;
      }
    }
    return selectedImage;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    let logoPath = localInput.logo;
    let faviconPath = localInput.favicon;

    // Upload Logo
    if (localInput.logo && typeof localInput.logo !== "string") {
      const uploadedLogoPath = await uploadImage(localInput.logo);
      if (!uploadedLogoPath) {
        setLoading(false);
        return; // Stop execution if upload fails
      }
      logoPath = uploadedLogoPath;
    }

    // Upload Favicon
    if (localInput.favicon && typeof localInput.favicon !== "string") {
      const uploadedFaviconPath = await uploadImage(localInput.favicon);
      if (!uploadedFaviconPath) {
        setLoading(false);
        return; // Stop execution if upload fails
      }
      faviconPath = uploadedFaviconPath;
    }

    // Prepare form data with uploaded image paths
    const formData = {
      type: "general",
      settings: {
        auto_responder_message: localInput?.auto_responder_message,
        meta_title: localInput.meta_title,
        meta_description: localInput.meta_description,
        topup_fee: parseFloat(localInput.top_up_fee),
        affiliate_commission: parseFloat(localInput.affiliate_commission),
        logo: logoPath, // Uploaded Admin Portal Logo path
        favicon: faviconPath, // Uploaded Admin Favicon path
      },
    };

    // Submit the updated form data
    const response = await _Website_Settings_List_Update(formData);
    if (response.code === 200) {
      setLocalInput({
        ...response.settings,
        top_up_fee: response?.settings?.topup_fee,
      });
      enqueueSnackbar("Settings updated successfully", {
        variant: "success",
      });
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
    }
    setLoading(false);
  };
  // console.log(
  //   localInput,
  //   "localInputlocalInputlocalInputlocalInputlocalInputlocalInputlocalInput"
  // );

  if (loading) {
    return <CustomCircularProgress />;
  }
  return (
    <form onSubmit={handleSubmit}>
      <div className="row mt-3">
        {/* <div className="col-md-6 mt-3">
          <FormControl fullWidth required>
            <InputLabel htmlFor="outlined-adornment-top-up-fee">
              Top Up Fee
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-top-up-fee"
              value={localInput?.top_up_fee || ""}
              type="number"
              onChange={(e) => handleChange(e, "top_up_fee")}
              name="top_up_fee"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              endAdornment={<InputAdornment position="end">%</InputAdornment>}
              label="Top Up Fee"
            />
          </FormControl>
        </div> */}

        <div className="col-md-6 d-flex align-items-center">
          <FormControl fullWidth required>
            <InputLabel htmlFor="outlined-adornment-affiliate-commission">
              Affiliate Commission
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-affiliate-commission"
              value={localInput?.affiliate_commission}
              type="number"
              onChange={(e) => handleChange(e, "affiliate_commission")}
              name="affiliate_commission"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              endAdornment={<InputAdornment position="end">%</InputAdornment>}
              label="Affiliate Commission"
            />
          </FormControl>
        </div>

        <div className="col-md-6">
          <FormInput
            fullWidth
            label="Meta Title"
            variant="outlined"
            value={localInput?.meta_title}
            onChange={(e) => handleChange(e, "meta_title")}
            required
          />
        </div>

        <div className="col-md-6 mt-1">
          <FormInput
            fullWidth
            label="Meta Description"
            variant="outlined"
            value={localInput?.meta_description}
            onChange={(e) => handleChange(e, "meta_description")}
            required
          />
        </div>
        <div className="col-md-12 mt-1">
          <FormInput
            fullWidth
            label="Auto Responder Message"
            variant="outlined"
            multline={true}
            value={localInput?.auto_responder_message}
            onChange={(e) => handleChange(e, "auto_responder_message")}
            required
          />
        </div>
        {/* Admin Portal Logo Upload */}
        {/* <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
          <div className="row w-100 div-style ms-0 pt-0">
            <div className="col-lg-5 col-md-5 col-sm-12">
              <p>Admin Portal Logo</p>
              <FormHelperText>
                Image Size (778 X 430) ("JPG", "JPEG", "PNG","WEBP")
              </FormHelperText>
            </div>
            <div className="col-lg-2 col-md-2 col-6">
              {Logo ? (
                <img className="image-border" src={Logo} height="50" />
              ) : (
                <>
                  {localInput?.logo && (
                    <img
                      className="image-border"
                      src={picBaseUrl + localInput?.logo}
                      height="50"
                    />
                  )}
                </>
              )}
            </div>
            <div className="col-lg-5 col-md-5 col-6 text-end pt-2">
              <label htmlFor="upload-logo">
                <Input
                  accept="image/*"
                  id="upload-logo"
                  type="file"
                  name="image"
                  onChange={(e) => fileChangedHandler("logo", e)}
                />
                <Button
                  className="small-contained-button"
                  startIcon={
                    <FileUploadIcon
                      sx={{
                        fill: "#fff",
                      }}
                    />
                  }
                  component="span"
                >
                  Upload
                </Button>
              </label>
            </div>
          </div>
        </div> */}

        {/* Admin Favicon Upload */}
        {/* <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
          <div className="row w-100 div-style ms-0 pt-0">
            <div className="col-lg-5 col-md-5 col-sm-12">
              <p>Admin Favicon</p>
              <FormHelperText>
                Image Size (32 X 32) ("JPG", "JPEG", "PNG","WEBP")
              </FormHelperText>
            </div>
            <div className="col-lg-2 col-md-2 col-6">
              {favIcon ? (
                <img className="image-border" src={favIcon} height="50" />
              ) : (
                <>
                  {localInput?.favicon && (
                    <img
                      className="image-border"
                      src={picBaseUrl + localInput?.favicon}
                      height="50"
                    />
                  )}
                </>
              )}
            </div>
            <div className="col-lg-5 col-md-5 col-6 text-end pt-2">
              <label htmlFor="upload-favicon">
                <Input
                  accept="image/*"
                  id="upload-favicon"
                  type="file"
                  name="image1"
                  onChange={(e) => fileChangedHandler("favicon", e)}
                />
                <Button
                  className="small-contained-button"
                  startIcon={
                    <FileUploadIcon
                      sx={{
                        fill: "#fff",
                      }}
                    />
                  }
                  component="span"
                >
                  Upload
                </Button>
              </label>
            </div>
          </div>
        </div> */}

        {/* Submit Button */}
        <div className="col-12 mt-3 text-end">
          <Button
            variant="contained"
            className="small-contained-button"
            type="submit"
          >
            Submit
          </Button>
        </div>
      </div>
    </form>
  );
};

export default DefaultSetting;
